import { POLL_THEME_MAPPING } from 'schema/src/kinds/poll/theme';
import {
  FALLBACK_TUI_FONTS,
  POLL_THEME_FONTS,
  ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_ANDROID,
  ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_DESKTOP,
  ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_IOS,
} from '../constants';
import { createStyleContent } from '../themeUtils';
import { FontsFamilies, FontsVariables, ThemeFonts } from '../types';

type ThemeFontsMapping = typeof POLL_THEME_MAPPING.fonts;

export const getFontsStyleContent = (fonts: ThemeFonts | undefined, fontMapping: ThemeFontsMapping) => {
  if (!fonts || Object.keys(fonts).length === 0) {
    return '';
  }

  const formattedFonts = {} as Record<FontsVariables, { desktop: string; mobile: string }>;

  for (const [fontVariable, { desktop, mobile }] of Object.entries(fonts.font_variables)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (desktop) {
      formattedFonts[fontVariable as FontsVariables] = {
        desktop: getFontDefinition({
          fontWeight: desktop.fontWeight,
          fontSize: desktop.fontSize,
          lineHeight: desktop.lineHeight,
          themeFontFamily: fonts.font_family,
          fontVariable,
        }),
        mobile: '',
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (mobile) {
      formattedFonts[fontVariable as FontsVariables] = {
        ...formattedFonts[fontVariable as FontsVariables],
        mobile: getFontDefinition({
          fontWeight: mobile.fontWeight,
          fontSize: mobile.fontSize,
          lineHeight: mobile.lineHeight,
          themeFontFamily: fonts.font_family,
          fontVariable,
        }),
      };
    }
  }

  const linkSet: Set<(typeof POLL_THEME_FONTS)[keyof typeof POLL_THEME_FONTS]> = new Set();
  const fontsLinks = Object.keys(POLL_THEME_FONTS);
  const fontFamilies = [fonts.font_family.heading_font_family, fonts.font_family.text_font_family];

  fontFamilies.forEach(fontFamily => {
    fontsLinks.forEach(font => {
      if (fontFamily.includes(font)) {
        linkSet.add(POLL_THEME_FONTS[font]);
      }
    });
  });

  loadFontLinks(linkSet);
  return getModernFontStyleContent(formattedFonts, fontMapping);
};

const getModernFontStyleContent = (
  fonts: Record<string, { desktop: string; mobile: string }>,
  fontMapping: ThemeFontsMapping,
) => {
  const desktopFontStyles: string[] = [];
  const mobileFontStyles: string[] = [];

  for (const [fontVariable, platforms] of Object.entries(fonts)) {
    const mappingDesktopTuiName = fontMapping[fontVariable as FontsVariables];
    if (platforms.desktop && mappingDesktopTuiName) {
      desktopFontStyles.push(`${mappingDesktopTuiName}: ${platforms.desktop};`);
    }

    const mappingMobileTuiName = fontMapping[fontVariable as FontsVariables];
    if (platforms.mobile && mappingMobileTuiName) {
      mobileFontStyles.push(`${mappingMobileTuiName}: ${platforms.mobile};`);
    }
  }

  const desktopFonts = createStyleContent(desktopFontStyles, ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_DESKTOP);

  const androidFonts = createStyleContent(mobileFontStyles, ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_ANDROID);
  const iosFonts = createStyleContent(mobileFontStyles, ROOT_TUI_DESKTOP_TYPOGRAPHY_SELECTOR_IOS);

  return `${desktopFonts} ${androidFonts} ${iosFonts}`;
};

const getFontDefinition = ({
  fontWeight,
  fontSize,
  lineHeight,
  themeFontFamily,
  fontVariable,
}: {
  fontWeight: string;
  fontSize: string;
  lineHeight: string;
  themeFontFamily: FontsFamilies;
  fontVariable: FontsVariables;
}): string => {
  const fontType = fontVariable.includes('heading') ? 'heading' : 'text';
  const fontFamily = fontType === 'heading' ? themeFontFamily.heading_font_family : themeFontFamily.text_font_family;
  const isTinkoffSansFontFamily = ['dsText', 'dsHeading'].includes(fontFamily);

  if (isTinkoffSansFontFamily) {
    return `${fontWeight} ${fontSize}px/${lineHeight}px ${getTuiFontFamily(fontType)}`;
  }

  return `${fontWeight} ${fontSize}px/${lineHeight}px ${fontFamily}, ${getTuiFontFamily(fontType)}`;
};

const getTuiFontFamily = (fontType: 'text' | 'heading') => FALLBACK_TUI_FONTS[fontType];

const loadFontLinks = (linkSet: Set<(typeof POLL_THEME_FONTS)[keyof typeof POLL_THEME_FONTS]>) => {
  linkSet.forEach(dynamicLoadCssFont);
};

const dynamicLoadCssFont = async (fontPath: (typeof POLL_THEME_FONTS)[keyof typeof POLL_THEME_FONTS]) => {
  try {
    switch (fontPath) {
      case 'Montserrat':
        await import('../../assets/fonts/Montserrat.css');
        break;
      case 'FactorA':
        await import('../../assets/fonts/FactorA.css');
        break;
      case 'RobotoFlex':
        await import('../../assets/fonts/RobotoFlex.css');
        break;
      case 'PfHighway':
        await import('../../assets/fonts/PfHighway.css');
        break;
      case 'NeueHaasUnica':
        await import('../../assets/fonts/NeueHaasUnica.css');
        break;
      case 'Graphik':
        await import('../../assets/fonts/Graphik.css');
        break;
      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Font ${fontPath} not supported`);
    }
  } catch (error) {
    console.error(`Failed to load: ${fontPath}.css`, error);
  }
};
